import { useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import {
  ShieldCheck, ShieldAlert, ArrowUpRight, ArrowDownRight, Sparkles, Cpu, ArrowRight,
  FileText, Building2, Gauge, Network, Clock, CheckCircle2, Lock, Database, ChevronRight, Share2, Loader2,
} from "lucide-react";
import { ScoreGauge } from "@/components/common/ScoreGauge";
import { StatusChip } from "@/components/common/StatusChip";
import { MatchChip } from "@/components/common/MatchChip";
import { Bar, DeltaPill } from "@/pages/_shared";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const CLASS_COLORS = {
  "PUBLIC": "#64748b", "INTERNAL": "#0ea5e9", "CONFIDENTIAL": "#8b5cf6",
  "SENSITIVE FINANCIAL": "#1a5fd0", "RESTRICTED IDENTITY": "#e11d48",
};

function Shell({ spec, children, icon: Icon }) {
  const p = spec.provenance || {};
  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white shadow-[0_1px_20px_-12px_rgba(15,23,42,0.25)] animate-fade-up" data-testid={`gen-component-${spec.type}`}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon size={15} className="shrink-0 text-[#1a5fd0]" />}
          <span className="truncate font-head text-[13px] font-bold text-slate-900">{spec.title}</span>
        </div>
        {p.data_class && (
          <span className="shrink-0 rounded-md px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider"
            style={{ background: (CLASS_COLORS[p.data_class] || "#64748b") + "14", color: CLASS_COLORS[p.data_class] || "#64748b" }}>
            {p.data_class}
          </span>
        )}
      </div>
      <div className="flex-1 p-4">{children}</div>
      {p.agent && (
        <div className="flex items-center gap-1.5 border-t border-slate-100 px-4 py-2 font-mono text-[9px] uppercase tracking-wider text-slate-400">
          <Cpu size={9} /> {p.agent}{p.state ? ` · ${p.state}` : ""}
        </div>
      )}
    </div>
  );
}

// ---------- renderers ----------
function ReadinessGauge({ spec }) {
  const d = spec.data;
  return (
    <Shell spec={spec} icon={Gauge}>
      <div className="flex flex-col items-center">
        <ScoreGauge score={d.score} scale={d.scale} tier={d.tier} size={150} testId="gen-readiness-gauge" />
        <div className="mt-2 flex items-center gap-2 text-[12px] text-slate-500">30-day <DeltaPill value={`+${d.delta_30d}`} /></div>
      </div>
    </Shell>
  );
}

function IdentityGraph({ spec }) {
  const d = spec.data;
  return (
    <Shell spec={spec} icon={Building2}>
      <div className="flex items-baseline gap-1.5"><span className="font-head text-3xl font-extrabold text-slate-900">{d.integrity_score}</span><span className="font-mono text-xs text-slate-400">/100 integrity</span></div>
      <div className="mt-2"><Bar pct={d.integrity_score} /></div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {d.nodes.slice(0, 6).map((n) => <span key={n.id} className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[9px] text-slate-600">{n.label}</span>)}
      </div>
      {d.issues?.length > 0 && <div className="mt-3 text-[11px] text-amber-600">⚠ {d.issues.join(" · ")}</div>}
    </Shell>
  );
}

function CashFlowChart({ spec }) {
  const d = spec.data;
  return (
    <Shell spec={spec} icon={Network}>
      <div className="mb-2 flex flex-wrap gap-3 text-[11px]">
        <span className="text-slate-500">Rev <b className="text-slate-800">{d.metrics.monthly_revenue}</b></span>
        <span className="text-slate-500">DSCR <b className="text-slate-800">{d.metrics.dscr}</b></span>
        <span className="text-slate-500">ADB <b className="text-slate-800">{d.metrics.avg_daily_balance}</b></span>
        <span className="text-slate-500">Runway <b className="text-slate-800">{d.metrics.cash_runway_days}d</b></span>
      </div>
      <div style={{ width: "100%", height: 160 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <AreaChart data={d.timeline} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
            <defs><linearGradient id="gcf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1a5fd0" stopOpacity={0.25} /><stop offset="100%" stopColor="#1a5fd0" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 11 }} formatter={(v) => `$${v}K`} />
            <Area type="monotone" dataKey="revenue" stroke="#1a5fd0" strokeWidth={2} fill="url(#gcf)" />
            <Area type="monotone" dataKey="expenses" stroke="#94a3b8" strokeWidth={1.5} fillOpacity={0} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Shell>
  );
}

function ComparisonTable({ spec }) {
  const d = spec.data;
  return (
    <Shell spec={spec} icon={Database}>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead><tr className="border-b border-slate-100 text-left font-mono text-[9px] uppercase tracking-wider text-slate-400">
            {d.columns.map((c) => <th key={c} className="pb-2 pr-2 font-medium">{c}</th>)}
          </tr></thead>
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={r.label || r.provider || i} className="border-b border-slate-50">
                <td className="py-2 pr-2 font-medium text-slate-800">{r.label}</td>
                {d.mode === "factors" && <><td className="py-2 pr-2 text-slate-700">{r.a}</td><td className="py-2 pr-2"><DeltaPill value={r.b} /></td><td className="py-2 text-slate-400">{r.weight}%</td></>}
                {d.mode === "revexp" && <><td className="py-2 pr-2 text-slate-600">{r.a}</td><td className="py-2 pr-2 text-slate-800 font-medium">{r.b}</td><td className={`py-2 font-mono ${r.delta < 0 ? "text-red-600" : "text-emerald-600"}`}>{r.delta > 0 ? "+" : ""}{r.delta}</td></>}
                {d.mode === "credit" && <><td className="py-2 pr-2 text-slate-500">{r.provider}</td><td className="py-2 pr-2 font-mono text-slate-400">{r.approved}</td><td className="py-2 pr-2 font-mono text-slate-400">{r.bureaus}</td><td className="py-2"><StatusChip state={r.state} /></td></>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d.disclaimer && <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-800">{d.disclaimer}</div>}
    </Shell>
  );
}

function CapitalMatch({ spec }) {
  return (
    <Shell spec={spec} icon={Sparkles}>
      <div className="space-y-2">
        {spec.data.options.map((o, i) => (
          <div key={o.name || i} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5">
            <div><div className="text-[13px] font-semibold text-slate-800">{o.name}</div><div className="text-[11px] text-slate-400">{o.amount} · {o.terms}</div></div>
            <MatchChip status={o.status} />
          </div>
        ))}
      </div>
    </Shell>
  );
}

const STAGES = ["BUILD", "PREPARE", "QUALIFY", "APPLY", "CAPITAL", "MONITOR", "GROW", "REFINANCE"];
function CapitalJourney({ spec }) {
  const d = spec.data;
  const idx = STAGES.indexOf(d.stage);
  const flow = [
    { k: "Current Position", v: d.current_position.summary },
    { k: "Readiness", v: `${d.readiness.score} · ${d.readiness.tier}` },
    { k: "Application Firewall", v: d.firewall },
    { k: "Next Action", v: d.next_action },
  ];
  return (
    <Shell spec={spec} icon={Network}>
      <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#1a5fd0]">Destination</span>
        <span className="font-head text-lg font-extrabold text-slate-900">{d.destination}</span>
        <span className="rounded-md bg-[#080d1a] px-2 py-0.5 font-mono text-[10px] font-bold text-white">{d.target_amount}</span>
      </div>
      {/* stage pipeline */}
      <div className="mb-4 flex items-center gap-1 overflow-x-auto pb-1">
        {STAGES.map((s, i) => (
          <div key={s} className="flex items-center gap-1">
            <span className={`rounded-md px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider ${i < idx ? "bg-emerald-50 text-emerald-600" : i === idx ? "bg-[#1a5fd0] text-white" : "bg-slate-100 text-slate-400"}`}>{s}</span>
            {i < STAGES.length - 1 && <ChevronRight size={11} className="text-slate-300" />}
          </div>
        ))}
      </div>
      {/* horizontal flow */}
      <div className="grid gap-2 sm:grid-cols-4">
        {flow.map((f) => (
          <div key={f.k} className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2.5">
            <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{f.k}</div>
            <div className="mt-0.5 text-[12px] font-medium text-slate-800 line-clamp-2">{f.v}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px] text-slate-400">{d.disclaimer}</div>
    </Shell>
  );
}

function AlertComp({ spec }) {
  const d = spec.data;
  const lvl = { "READY": { c: "#059669", i: ShieldCheck }, "REVIEW FIRST": { c: "#d97706", i: ShieldAlert }, "NOT READY": { c: "#dc2626", i: ShieldAlert } }[d.level] || { c: "#d97706", i: ShieldAlert };
  const Icon = lvl.i;
  return (
    <Shell spec={spec} icon={Icon}>
      <div className="mb-3 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider" style={{ background: lvl.c + "14", color: lvl.c }}>
        <Icon size={14} /> {d.level}
      </div>
      <div className="space-y-2">
        {d.items.map((it, i) => (
          <div key={it.name || i} className="rounded-lg border border-slate-100 px-3 py-2">
            <div className="flex items-center justify-between"><span className="text-[12px] font-semibold text-slate-800">{it.name}</span><span className="font-mono text-[9px] uppercase text-slate-400">{it.status}</span></div>
            <div className="text-[11px] text-slate-500">{it.detail}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-slate-400">{d.note}</div>
    </Shell>
  );
}

function ScenarioSimulator({ spec }) {
  const [active, setActive] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const base = spec.data.base_score;

  const toggle = async (id) => {
    const next = active.includes(id) ? active.filter((x) => x !== id) : [...active, id];
    setActive(next);
    setLoading(true);
    try {
      const { data } = await api.post("/capital-os/simulate", { levers: next });
      setResult(data);
    } catch (err) { void err; } finally { setLoading(false); }
  };

  const score = result ? result.modeled_score : base;
  const delta = result ? result.delta : 0;
  return (
    <Shell spec={spec} icon={Gauge}>
      <div className="flex items-center gap-4">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Modeled readiness</div>
          <div className="flex items-baseline gap-2">
            <span className="font-head text-3xl font-extrabold text-slate-900 tabular-nums">{score}</span>
            {delta !== 0 && <DeltaPill value={`${delta > 0 ? "+" : ""}${delta}`} />}
          </div>
          <div className="font-mono text-[10px] text-slate-400">base {base}{loading ? " · modeling…" : ""}</div>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {spec.data.levers.map((l) => (
          <button key={l.id} onClick={() => toggle(l.id)} data-testid="sim-lever"
            className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-[12px] transition-colors ${active.includes(l.id) ? "border-[#1a5fd0] bg-blue-50 text-slate-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            {l.label}
            <span className={`h-3.5 w-3.5 rounded-full border ${active.includes(l.id) ? "border-[#1a5fd0] bg-[#1a5fd0]" : "border-slate-300"}`} />
          </button>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-slate-400">{spec.data.note}</div>
    </Shell>
  );
}

function DocumentChecklist({ spec }) {
  return (
    <Shell spec={spec} icon={FileText}>
      <div className="space-y-1.5">
        {spec.data.items.map((it, i) => (
          <div key={it.name || i} className="flex items-center justify-between rounded-lg bg-slate-50/70 px-3 py-2">
            <span className="truncate text-[12px] text-slate-700">{it.name}</span>
            <StatusChip state={it.state} />
          </div>
        ))}
      </div>
    </Shell>
  );
}

const KIND = { up: "text-emerald-500", down: "text-red-500", watch: "text-amber-500", recurring: "text-slate-400" };
function Timeline({ spec }) {
  return (
    <Shell spec={spec} icon={Clock}>
      <div className="space-y-2.5">
        {spec.data.events.map((e, i) => (
          <div key={`${e.label}-${e.when}-${i}`} className="flex items-start gap-3">
            <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${KIND[e.kind]?.replace("text", "bg") || "bg-slate-300"}`} />
            <div><div className="text-[12px] font-medium text-slate-800">{e.label}</div><div className="font-mono text-[10px] text-slate-400">{e.when}</div></div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function NextAction({ spec }) {
  return (
    <Shell spec={spec} icon={ArrowRight}>
      <p className="text-[13px] leading-relaxed text-slate-700">{spec.data.action}</p>
      <button className="mt-3 flex items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-3.5 py-2 text-[12px] font-semibold text-white" data-testid="gen-next-action-btn">
        Do this next <ArrowRight size={14} />
      </button>
    </Shell>
  );
}

function AiExplanation({ spec }) {
  return (
    <Shell spec={spec} icon={Sparkles}>
      <p className="text-[13px] leading-relaxed text-slate-700">{spec.data.text}</p>
      {spec.data.model && <div className="mt-2 flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-slate-400"><Cpu size={9} /> {spec.data.model}</div>}
    </Shell>
  );
}

function AgentActivity({ spec }) {
  return (
    <Shell spec={spec} icon={Cpu}>
      <div className="space-y-2">
        {spec.data.steps.map((s, i) => (
          <div key={s.agent || i} className="flex items-center justify-between rounded-lg bg-slate-50/70 px-3 py-2">
            <div><div className="text-[12px] font-semibold text-slate-800">{s.agent}</div><div className="text-[11px] text-slate-500">{s.text}</div></div>
            <span className={`font-mono text-[9px] uppercase ${s.state === "processing" ? "text-blue-600" : s.state === "pending" ? "text-amber-600" : "text-emerald-600"}`}>● {s.state}</span>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function CapitalPassport({ spec }) {
  const navigate = useNavigate();
  const [shared, setShared] = useState(() => Object.fromEntries(spec.data.sections.map((s) => [s.key, s.shared])));
  const toggle = async (key) => {
    const next = !shared[key];
    setShared((p) => ({ ...p, [key]: next }));
    try { await api.put("/passport/permissions", { section_key: key, shared: next }); toast.success(`Sharing ${next ? "enabled" : "disabled"}`); }
    catch { toast.error("Could not update"); setShared((p) => ({ ...p, [key]: !next })); }
  };
  return (
    <Shell spec={spec} icon={Lock}>
      <div className="grid gap-2 sm:grid-cols-2">
        {spec.data.sections.map((s) => (
          <div key={s.key} className="rounded-lg border border-slate-100 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-semibold text-slate-800">{s.title}</span>
              <button onClick={() => toggle(s.key)} data-testid="passport-share-toggle"
                className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase ${shared[s.key] ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                <Share2 size={9} />{shared[s.key] ? "Shared" : "Private"}
              </button>
            </div>
            <div className="mt-1 space-y-0.5">
              {s.fields.slice(0, 3).map((f) => <div key={f.label} className="flex justify-between text-[11px]"><span className="text-slate-400">{f.label}</span><span className="text-slate-700">{f.value}</span></div>)}
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => navigate("/capital-passport")} data-testid="passport-manage-btn"
        className="mt-3 flex items-center gap-1.5 text-[12px] font-semibold text-[#1a5fd0]">
        <Share2 size={13} /> Manage sharing & partners
      </button>
      <div className="mt-1 text-[10px] text-slate-400">{spec.data.note}</div>
    </Shell>
  );
}

function ApprovalRequest({ spec }) {
  const d = spec.data || {};
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/approvals", {
        title: d.approval_title || "Approval required",
        description: d.action || "Requires explicit human approval.",
        action_type: d.action_type || "generic",
        data_class: d.data_class || "CONFIDENTIAL",
        requested_by: spec.provenance?.agent || "Morgan — AI General Manager",
      });
      setSent(true);
      toast.success("Sent to your Approval Inbox");
    } catch { toast.error("Could not create approval"); } finally { setBusy(false); }
  };
  return (
    <Shell spec={spec} icon={ShieldAlert}>
      <p className="text-[13px] text-slate-700">{d.action || "This action requires your explicit approval before MMT executes it."}</p>
      {!sent ? (
        <button onClick={send} disabled={busy} data-testid="gen-approval-send-btn"
          className="mt-3 flex items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-3.5 py-2 text-[12px] font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />} Send to Approval Inbox
        </button>
      ) : (
        <button onClick={() => navigate("/approvals")} data-testid="gen-approval-goto-btn"
          className="mt-3 flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-[12px] font-semibold text-slate-700">
          <CheckCircle2 size={13} className="text-emerald-500" /> In your Approval Inbox — review
        </button>
      )}
    </Shell>
  );
}

function ProvenancePanel({ spec }) {
  return (
    <Shell spec={spec} icon={Database}>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {spec.data.rows.map((r, i) => (
          <div key={r.component || i} className="rounded-lg border border-slate-100 px-3 py-2">
            <div className="text-[12px] font-semibold text-slate-800">{r.component}</div>
            <div className="mt-0.5 text-[10px] text-slate-500">{(r.sources || []).join(" · ")}</div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="font-mono text-[8px] font-bold uppercase" style={{ color: CLASS_COLORS[r.data_class] || "#64748b" }}>{r.data_class}</span>
              {r.state && <StatusChip state={r.state} />}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

export const RENDERERS = {
  readiness_gauge: ReadinessGauge,
  identity_graph: IdentityGraph,
  cash_flow_chart: CashFlowChart,
  comparison_table: ComparisonTable,
  capital_match: CapitalMatch,
  capital_journey: CapitalJourney,
  alert: AlertComp,
  scenario_simulator: ScenarioSimulator,
  document_checklist: DocumentChecklist,
  timeline: Timeline,
  next_action: NextAction,
  ai_explanation: AiExplanation,
  agent_activity: AgentActivity,
  capital_passport: CapitalPassport,
  approval_request: ApprovalRequest,
  provenance_panel: ProvenancePanel,
};

const SPAN = { full: "col-span-6", half: "col-span-6 lg:col-span-3", third: "col-span-6 sm:col-span-3 lg:col-span-2" };

export function WorkspaceComponent({ spec }) {
  const R = RENDERERS[spec.type];
  if (!R) return null; // allowlist enforced client-side too
  return <div className={SPAN[spec.size] || "col-span-6"}><R spec={spec} /></div>;
}
